/**
 * The results tray's XP beat as ONE clock.
 *
 * `e` is ms since the tray showed. The count, the tray's numbers, the level-up
 * flip, the full-screen rank-up and the tray's own card are all `beatAt(plan,
 * e)` — nothing else in the beat keeps time. That is what makes "two ranks on
 * screen at once" unrepresentable rather than fixed: a skip moves the clock
 * (`BeatClock.jumpTo`), a capture pins it (`pin`), and every view reads the
 * same frame from the same number.
 *
 * Ported from Blockari's `ceremony/beat.ts` over 21 Wild's ladder, with the
 * count's length and delay kept so the two games count at the same speed.
 */

import { rankFor, xpLevelFor, xpThreshold, type Rank } from '@/game/xp';
import { planCeremony, type CeremonyPlan } from './plan';
import {
  LEVEL_T,
  RANK_T,
  levelFrameAt,
  rankFrameAt,
  restPose,
  type CardPose,
  type LevelFrame,
  type RankFrame,
} from './timeline';

/** The count starts this long after the tray shows (its rise-in is 420 ms). */
export const START_DELAY_MS = 350;
/** The count's length. */
export const COUNT_MS = 1200;

export interface XpNumbers {
  readonly xpBefore: number;
  readonly xpAfter: number;
  readonly levelBefore: number;
  readonly levelAfter: number;
}

export interface Shown {
  readonly xp: number;
  readonly level: number;
  readonly rank: Rank;
  readonly prev: number;
  readonly next: number;
}

export function shownFor(xp: number, level = xpLevelFor(xp)): Shown {
  return {
    xp,
    level,
    rank: rankFor(level),
    prev: xpThreshold(level),
    next: xpThreshold(level + 1),
  };
}

export interface BeatPlan {
  readonly gained: XpNumbers;
  readonly ceremony: CeremonyPlan | null;
  /** When the count reaches the ceremony's threshold (ms since the tray showed); Infinity without one. */
  readonly crossMs: number;
  /** The rank-up's end state / the level-up's landing (ms since the tray showed); the count's end without one. */
  readonly endMs: number;
}

const easeOutCubic = (u: number): number => 1 - (1 - u) ** 3;

/** The time (ms since the tray showed) at which the count reaches an XP total. */
function timeOf(g: XpNumbers, xp: number): number {
  const ue = (xp - g.xpBefore) / Math.max(1, g.xpAfter - g.xpBefore);
  const u = 1 - Math.cbrt(Math.max(0, 1 - ue));
  return START_DELAY_MS + u * COUNT_MS;
}

export function beatPlan(gained: XpNumbers): BeatPlan {
  const ceremony = planCeremony(gained.levelBefore, gained.levelAfter);
  const countEnd = START_DELAY_MS + COUNT_MS;
  if (!ceremony) return { gained, ceremony, crossMs: Infinity, endMs: countEnd };
  const crossMs = timeOf(gained, xpThreshold(ceremony.crossLevel));
  const endMs =
    ceremony.kind === 'rank' ? crossMs + RANK_T.end : Math.max(countEnd, crossMs + LEVEL_T.end);
  return { gained, ceremony, crossMs, endMs };
}

export interface BeatState {
  /** What the tray's numbers read (the bar, the name, the chip). */
  readonly shown: Shown;
  /** The full-screen rank-up at this instant (from the crossing on), or null. */
  readonly rank: RankFrame | null;
  /** The level-up flip while it runs, or null. */
  readonly level: LevelFrame | null;
  /** The tray's card and its pips. */
  readonly stage: CardPose;
  readonly stagePips: number;
  /** Everything has landed (the count, and any ceremony's end state). */
  readonly settled: boolean;
}

export function beatAt(bp: BeatPlan, e: number): BeatState {
  const g = bp.gained;
  const c = bp.ceremony;
  const u = Math.max(0, Math.min(1, (e - START_DELAY_MS) / COUNT_MS));
  const xp = u >= 1 ? g.xpAfter : g.xpBefore + (g.xpAfter - g.xpBefore) * easeOutCubic(u);
  let shown = shownFor(xp, Math.max(g.levelBefore, Math.min(g.levelAfter, xpLevelFor(xp))));
  const since = e - bp.crossMs;
  if (c?.kind === 'rank') {
    if (since >= RANK_T.swap) {
      // Under the veil from the swap on, the tray already reads the final state.
      shown = shownFor(g.xpAfter, g.levelAfter);
    } else if (since >= 0) {
      // From the crossing to the swap: held full on the old span, the old rank.
      shown = shownFor(xpThreshold(c.crossLevel), c.crossLevel - 1);
    }
    const rank = since >= 0 ? rankFrameAt(c, since) : null;
    return {
      shown,
      rank,
      level: null,
      stage: restPose(rank ? rank.card.rank : shown.rank),
      stagePips: rank?.swapped ? c.tierAfter : shown.rank.tier,
      settled: e >= bp.endMs,
    };
  }
  if (c?.kind === 'level') {
    const level = since >= 0 && since < LEVEL_T.end ? levelFrameAt(c, since) : null;
    // While the card turns the tray holds the crossing (bar full on the old
    // span, the old tier); the landing reveals the new tier — the name, the
    // chip and the pip together.
    if (since >= 0 && since < LEVEL_T.pip)
      shown = shownFor(xpThreshold(c.crossLevel), c.crossLevel - 1);
    return {
      shown,
      rank: null,
      level,
      stage: level ? level.card : restPose(since >= 0 ? c.in : c.out),
      // The pips read the count's own tier: they light with the name and the chip, never apart.
      stagePips: shown.rank.tier,
      settled: e >= bp.endMs,
    };
  }
  return {
    shown,
    rank: null,
    level: null,
    stage: restPose(shown.rank),
    stagePips: shown.rank.tier,
    settled: e >= bp.endMs,
  };
}

/**
 * The beat's one clock. `elapsed()` is ms since `start()`; a skip moves the
 * origin (`jumpTo`), a capture pins it (`pin`). Nothing else in the beat reads
 * a time.
 */
export class BeatClock {
  #origin = Number.NaN;
  #pinned: number | null = null;
  readonly #now: () => number;

  constructor(now: () => number = () => performance.now()) {
    this.#now = now;
  }

  start(): void {
    this.#origin = this.#now();
  }

  get started(): boolean {
    return this.#pinned !== null || !Number.isNaN(this.#origin);
  }

  elapsed(): number {
    if (this.#pinned !== null) return this.#pinned;
    return Number.isNaN(this.#origin) ? 0 : this.#now() - this.#origin;
  }

  /** Skip: the clock reads `ms` from now on (never backwards). */
  jumpTo(ms: number): void {
    if (ms <= this.elapsed()) return;
    if (this.#pinned !== null) this.#pinned = ms;
    else this.#origin = this.#now() - ms;
  }

  /** Capture: freeze the clock at `ms`. */
  pin(ms: number): void {
    this.#pinned = ms;
  }

  /** Tooling: unpin and run live from `ms`. */
  resume(ms: number): void {
    this.#pinned = null;
    this.#origin = this.#now() - ms;
  }

  get isPinned(): boolean {
    return this.#pinned !== null;
  }
}
